import { CircularProgress } from "@mui/material";
import DeleteIcon from "@/components/app/DeleteIcon";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import { StyleAddButton } from "@/pages/app/provider/scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import SectionAccordion from "./SectionAccordion";
import { useRegisterSectionSave } from "../pending-section-saves-context";
import AllergyEntryRow from "./AllergyEntryRow";
import { useAllergySection } from "../hooks/use-allergy-section";
import { buildAllergyEntries } from "../readmission-f2f-summary-utils";
import {
  ALLERGY_ADD_BUTTON_LABEL,
  ALLERGY_SEVERITY_OPTIONS,
  FREE_TEXT_LABEL,
  FREE_TEXT_PLACEHOLDER,
} from "../readmission-f2f-constant";
import MicroTextArea from "@/components/MicroTextArea";
import { useDraftValue } from "@/hooks/use-draft-value";

export interface AllergySectionProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  freeNoteValue?: string;
  onFreeNoteChange?: (value: string) => void;
}

const AllergySection = ({
  title,
  expanded,
  onToggle,
  freeNoteValue,
  onFreeNoteChange,
}: AllergySectionProps) => {
  const {
    rows,
    draft,
    draftError,
    isLoading,
    updateRow,
    updateDraft,
    addDraft,
    saveRow,
    removeRow,
  } = useAllergySection();
  // A typed but not yet added row is committed when the note is saved or signed.
  useRegisterSectionSave(title, !!draft.allergy.trim(), addDraft);
  const freeNote = useDraftValue(freeNoteValue ?? "", (value) =>
    onFreeNoteChange?.(value)
  );

  return (
    <SectionAccordion
      title={title}
      expanded={expanded}
      onToggle={onToggle}
      summaryLines={buildAllergyEntries(rows, freeNoteValue)}
    >
      {isLoading ? (
        <Flex justifyContent="center" p={2}>
          <CircularProgress size={22} />
        </Flex>
      ) : (
        <>
          {rows.map((row) => (
            <AllergyEntryRow
              key={row.id}
              row={row}
              severityOptions={ALLERGY_SEVERITY_OPTIONS}
              onChange={(patch) => updateRow(row.id, patch)}
              onCommit={saveRow}
              action={
                <DeleteIcon
                  aria-label="Remove allergy"
                  onClick={() => removeRow(row)}
                />
              }
            />
          ))}
          <AllergyEntryRow
            key={draft.id}
            row={draft}
            isDraft
            severityOptions={ALLERGY_SEVERITY_OPTIONS}
            error={draftError?.allergy}
            severityError={draftError?.severity}
            onChange={updateDraft}
            action={
              <StyleAddButton
                w="140px"
                onClick={addDraft}
                label={ALLERGY_ADD_BUTTON_LABEL}
              />
            }
          />
        </>
      )}

      <Flex flexDirection="column" gap={1}>
        <Text fontWeight="bold">{FREE_TEXT_LABEL}</Text>
        <MicroTextArea
          rows={4}
          variant="outlined"
          name="allergyFreeText"
          placeholder={FREE_TEXT_PLACEHOLDER}
          value={freeNote.draft}
          onChange={(event) => freeNote.setDraft(event.target.value)}
          onBlur={freeNote.commit}
        />
      </Flex>
    </SectionAccordion>
  );
};

export default AllergySection;
